/**
 * Holographic Chat Orb - Floating Chat Widget
 * Design: "Electric Scholar" theme with glassmorphism
 * Position: Fixed above sticky CTA bar on mobile
 */
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Headphones,
  Loader2,
  Minimize2,
  Maximize2,
  Clock,
  Phone,
  Sparkles
} from 'lucide-react';

const API_URL = window.location.origin;
const WHATSAPP_NUMBER = "919636957799"; // UniXperts WhatsApp

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatMode, setChatMode] = useState('ai'); // ai or human
  const [counselorName, setCounselorName] = useState(null);
  const [isWorkHours, setIsWorkHours] = useState(true);
  const [showWhatsAppOption, setShowWhatsAppOption] = useState(false);
  const [showPortalLink, setShowPortalLink] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [visitorInfo, setVisitorInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [showContactForm, setShowContactForm] = useState(false);
  const messagesEndRef = useRef(null);
  const lastScrollY = useRef(0);
  const chatContainerRef = useRef(null);

  // Handle mobile keyboard viewport changes
  useEffect(() => {
    if (!isOpen || isMinimized) return;
    
    const handleResize = () => {
      // Force scroll to bottom when keyboard opens/closes
      if (chatContainerRef.current) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }
    };

    // Use visualViewport API for better mobile keyboard handling
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport.removeEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isOpen, isMinimized]);

  // Track scroll direction for opacity effect
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrollingDown(currentScrollY > lastScrollY.current && currentScrollY > 100);
      lastScrollY.current = currentScrollY;
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start chat session
  const startSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_name: visitorInfo.name || null,
          visitor_email: visitorInfo.email || null,
          visitor_phone: visitorInfo.phone || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setIsWorkHours(data.is_work_hours);
        setMessages([{
          id: 'greeting',
          content: data.greeting,
          sender: 'ai',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Failed to start chat session:', error);
    }
  };

  // Open chat and start session
  const handleOpen = () => {
    setIsOpen(true);
    if (!sessionId) {
      startSession();
    }
  };

  // Listen for global "open-ai-chat" events so other components (e.g. the
  // "Talk to AI Counselor" CTAs on public pages) can programmatically open
  // the widget without needing a direct ref.
  useEffect(() => {
    const handleGlobalOpen = () => {
      setIsOpen(true);
      setIsMinimized(false);
      if (!sessionId) {
        startSession();
      }
    };
    window.addEventListener('open-ai-chat', handleGlobalOpen);
    return () => window.removeEventListener('open-ai-chat', handleGlobalOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Send message
  const sendMessage = async () => {
    if (!inputValue.trim() || !sessionId || isLoading) return;

    const userMessage = {
      id: `msg_${Date.now()}`,
      content: inputValue,
      sender: 'visitor',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: inputValue,
          session_id: sessionId,
          visitor_name: visitorInfo.name || null,
          visitor_email: visitorInfo.email || null,
          visitor_phone: visitorInfo.phone || null
        })
      });

      if (res.ok) {
        const data = await res.json();
        
        if (data.response) {
          const aiMessage = {
            id: `msg_${Date.now()}_ai`,
            content: data.response,
            sender: data.escalated ? 'system' : 'ai',
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, aiMessage]);
        }

        if (data.mode) {
          setChatMode(data.mode);
        }

        if (data.counselor) {
          setCounselorName(data.counselor);
        }

        // Show WhatsApp option when escalation is triggered
        if (data.escalation_attempted || data.request_contact_info) {
          setShowContactForm(true);
          // Don't show WhatsApp as primary - human will join this chat
          // setShowWhatsAppOption(true);
        }

        // Show Student Portal link
        if (data.show_portal_link) {
          setShowPortalLink(true);
        }

        setIsWorkHours(data.is_work_hours);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        content: "Sorry, I couldn't send your message. Please try again.",
        sender: 'system',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitContactInfo = async () => {
    if (visitorInfo.email || visitorInfo.phone) {
      // Update session with contact info
      await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: `Contact info: ${visitorInfo.name}, ${visitorInfo.email}, ${visitorInfo.phone}`,
          session_id: sessionId,
          visitor_name: visitorInfo.name,
          visitor_email: visitorInfo.email,
          visitor_phone: visitorInfo.phone
        })
      });

      setShowContactForm(false);
      // Human will take over in this chat, no need for WhatsApp
      setMessages(prev => [...prev, {
        id: `contact_${Date.now()}`,
        content: "Thank you! A counselor will join this chat shortly to assist you. Please continue your conversation here.",
        sender: 'ai',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  // Generate WhatsApp link with context
  const getWhatsAppLink = () => {
    const lastMessages = messages.slice(-3).map(m => m.content).join(' | ');
    const name = visitorInfo.name || 'Student';
    const message = encodeURIComponent(
      `Hi! I'm ${name}. I was chatting on your website about studying abroad.\n\nContext: ${lastMessages.substring(0, 200)}...\n\nCan you help me further?`
    );
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  };

  // Chat bubble button when closed - Holographic Orb Design
  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        aria-label="Open AI Chat"
        className={`
          fixed z-50 
          bottom-[90px] right-4 
          md:bottom-8 md:right-8
          h-14 w-14 
          rounded-full 
          bg-gradient-to-tr from-ux-cyan to-ux-blue
          border border-white/20
          shadow-lg shadow-ux-cyan/30
          hover:shadow-xl hover:shadow-ux-cyan/40
          hover:scale-110 
          active:scale-95
          transition-all duration-300 ease-out
          flex items-center justify-center
          backdrop-blur-sm
          ${isScrollingDown ? 'opacity-50' : 'opacity-100'}
        `}
        data-testid="chat-widget-btn"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        {/* AI Badge */}
        <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-ux-violet text-[10px] font-bold text-white ring-2 ring-[#1a2830]">
          AI
        </span>
        {/* Pulse ring animation */}
        <span className="absolute inset-0 rounded-full bg-ux-cyan/20 animate-ping" style={{ animationDuration: '2s' }} />
      </button>
    );
  }

  return (
    <div 
      className={`
        fixed z-50 
        ${isMinimized 
          ? 'bottom-[90px] right-4 md:bottom-8 md:right-8 w-64' 
          : 'bottom-4 left-4 right-4 top-4 md:inset-auto md:bottom-8 md:right-8 md:w-[400px] md:h-[580px] md:top-auto md:left-auto'
        }
        transition-all duration-300 ease-out
      `}
      data-testid="chat-widget"
    >
      <Card className={`
        h-full w-full 
        overflow-hidden 
        flex flex-col
        ${isMinimized ? 'rounded-full' : 'rounded-2xl'}
        border border-white/10
        bg-[#1a2830]
        shadow-2xl shadow-ux-cyan/20
      `}>
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-ux-cyan via-ux-blue to-ux-violet p-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/20">
              {chatMode === 'ai' ? (
                <Sparkles className="w-5 h-5 text-white" />
              ) : (
                <Headphones className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                {chatMode === 'ai' ? 'UniXperts AI' : counselorName || 'Counselor'}
                {chatMode === 'ai' && (
                  <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">GPT</span>
                )}
              </h3>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-ux-green animate-pulse" />
                <span className="text-white/80 text-xs">
                  {chatMode === 'ai' ? 'AI Assistant' : 'Live Chat'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              {isMinimized ? (
                <Maximize2 className="w-4 h-4 text-white" />
              ) : (
                <Minimize2 className="w-4 h-4 text-white" />
              )}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

      {!isMinimized && (
        <>
          {/* Work Hours Indicator */}
          {!isWorkHours && (
            <div className="flex-shrink-0 bg-ux-violet/10 px-4 py-2 flex items-center gap-2 text-ux-violet text-xs border-b border-white/5">
              <Clock className="w-3 h-3" />
              <span>Counselors offline (Mon-Sat, 10AM-6PM IST). AI is here 24/7!</span>
            </div>
          )}

          {/* Messages */}
          <div ref={chatContainerRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4 bg-[#0f1a1f]">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === 'visitor' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.sender === 'visitor'
                      ? 'bg-gradient-to-r from-ux-cyan to-ux-blue text-white rounded-br-sm'
                      : msg.sender === 'system'
                      ? 'bg-ux-violet/20 text-white border border-ux-violet/30 rounded-bl-sm'
                      : 'bg-[#233842] text-white border border-white/10 rounded-bl-sm'
                  }`}
                >
                  {msg.sender !== 'visitor' && (
                    <div className="flex items-center gap-1.5 mb-1">
                      {msg.sender === 'ai' ? (
                        <Sparkles className="w-3 h-3 text-ux-cyan" />
                      ) : msg.sender === 'counselor' ? (
                        <User className="w-3 h-3 text-ux-violet" />
                      ) : null}
                      <span className="text-xs font-medium text-white/60">
                        {msg.sender === 'ai' ? 'AI Assistant' : msg.sender === 'counselor' ? msg.counselor_name : 'System'}
                      </span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-[#233842] border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-ux-cyan" />
                    <span className="text-sm text-white/60">Typing...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Form */}
            {showContactForm && (
              <div className="bg-[#233842] border border-white/10 rounded-xl p-4 space-y-3">
                <p className="text-sm font-medium text-white">
                  Leave your contact details for a callback:
                </p>
                <Input
                  placeholder="Your Name"
                  value={visitorInfo.name}
                  onChange={(e) => setVisitorInfo(prev => ({ ...prev, name: e.target.value }))}
                  className="text-sm bg-[#0f1a1f] border-white/10 text-white placeholder:text-white/40"
                />
                <Input
                  placeholder="Email Address"
                  type="email"
                  value={visitorInfo.email}
                  onChange={(e) => setVisitorInfo(prev => ({ ...prev, email: e.target.value }))}
                  className="text-sm bg-[#0f1a1f] border-white/10 text-white placeholder:text-white/40"
                />
                <Input
                  placeholder="Phone Number"
                  type="tel"
                  value={visitorInfo.phone}
                  onChange={(e) => setVisitorInfo(prev => ({ ...prev, phone: e.target.value }))}
                  className="text-sm bg-[#0f1a1f] border-white/10 text-white placeholder:text-white/40"
                />
                <Button 
                  onClick={submitContactInfo}
                  className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white text-sm font-medium"
                >
                  Request Callback
                </Button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex-shrink-0 p-4 bg-[#1a2830] border-t border-white/10 rounded-b-2xl">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Type your message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                className="flex-1 min-w-0 text-sm bg-[#0f1a1f] border-white/10 text-white placeholder:text-white/40 focus:border-ux-cyan"
                data-testid="chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="flex-shrink-0 bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white rounded-full w-10 h-10 p-0 shadow-lg shadow-ux-cyan/30"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            
            {/* WhatsApp Escalation Option - Secondary/Optional */}
            {showWhatsAppOption && (
              <a
                href={getWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-center justify-center gap-2 w-full py-2 border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-xl font-medium text-sm transition-colors"
                data-testid="chat-whatsapp-btn"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Or continue on WhatsApp
              </a>
            )}

            {/* Student Portal Link */}
            {showPortalLink && (
              <a
                href="/contact"
                className="mt-2 flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white rounded-xl font-medium text-sm transition-colors shadow-lg"
                data-testid="chat-portal-btn"
              >
                <Sparkles className="w-4 h-4" />
                Talk to a Counsellor
              </a>
            )}
            
            <p className="text-xs text-center text-white/30 mt-3">
              Powered by UniXperts AI
            </p>
          </div>
        </>
      )}
      </Card>
    </div>
  );
};

export default ChatWidget;
